import React, { useEffect, useMemo, useState } from 'react';
import { SendIcon, PlusIcon, FileTextIcon, ChevronDownIcon, ChevronUpIcon, Trash2Icon, PencilIcon, LockIcon, CalendarPlusIcon, CheckCircleIcon, ListIcon, CalendarDaysIcon, BarChart3Icon, TrendingUpIcon, UsersIcon } from 'lucide-react';
import { User, Store, FilterState, Province, StoreTier, MediaType, SlotStatus, BookingSlot, BrandRequest, Brand } from '../data/types';
import { Header } from '../components/layout/Header';
import { FilterBar } from '../components/layout/FilterBar';
import { UrgencyBanner } from '../components/dashboard/UrgencyBanner';
import { StoreTable } from '../components/dashboard/StoreTable';
import { DashboardCalendar } from '../components/dashboard/DashboardCalendar';
import { AdminControls } from '../components/admin/AdminControls';
import { BrandRequestModal } from '../components/admin/BrandRequestModal';
import { PreBookModal } from '../components/admin/PreBookModal';
import { Button } from '../components/ui/Button';
import { Tooltip } from '../components/ui/Tooltip';
interface AdminDashboardPageProps {
  user: User;
  stores: Store[];
  brandRequests: BrandRequest[];
  onLogout: () => void;
  onStoreSelect: (store: Store) => void;
  onNavigate: (view: any) => void;
  onSendRequest: (request: any) => void;
  onUpdateRequest: (requestId: string, request: any, sendNow?: boolean) => void;
  onActivateRequest: (requestId: string) => void;
  onRemoveRequest: (requestId: string) => void;
  onUpdateSlot: (storeId: string, slotId: string, updates: Partial<BookingSlot>) => void;
  activityLog?: any;
  brands?: Brand[];
}
export function AdminDashboardPage({
  user,
  stores,
  brandRequests,
  onLogout,
  onStoreSelect,
  onNavigate,
  onSendRequest,
  onUpdateRequest,
  onActivateRequest,
  onRemoveRequest,
  onUpdateSlot,
  activityLog,
  brands
}: AdminDashboardPageProps) {
  const [activeTab, setActiveTab] = useState<'stores' | 'calendar'>('stores');
  const [filters, setFilters] = useState<FilterState>({
    province: 'all',
    tier: 'all',
    mediaType: 'all',
    availability: 'all'
  });
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [editingDraft, setEditingDraft] = useState<BrandRequest | null>(null);
  const [showDrafts, setShowDrafts] = useState(false);
  const [preBookModal, setPreBookModal] = useState<{
    isOpen: boolean;
    mode: 'prebook' | 'block';
  }>({
    isOpen: false,
    mode: 'prebook'
  });
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({
      message,
      type
    });
    setTimeout(() => setToast(null), 3000);
  };
  // Filter stores from props (replaces useStores hook)
  const filteredStores = useMemo(() => {
    return stores.filter(store => {
      if (filters.province !== 'all' && store.province !== filters.province) return false;
      if (filters.tier !== 'all' && store.tier !== filters.tier) return false;
      if (filters.mediaType !== 'all') {
        const hasMediaType = store.slots.some(slot => slot.mediaType === filters.mediaType);
        if (!hasMediaType) return false;
      }
      if (filters.availability !== 'all') {
        const slotsToCheck = filters.mediaType !== 'all' ? store.slots.filter(slot => slot.mediaType === filters.mediaType) : store.slots;
        const hasMatchingStatus = slotsToCheck.some(slot => slot.status === filters.availability);
        if (!hasMatchingStatus) return false;
      }
      return true;
    });
  }, [stores, filters]);
  // Calculate booked count from managed stores (replaces countBookedThisWeek)
  const bookedCount = useMemo(() => {
    const currentWeek = 1;
    return stores.reduce((count, store) => {
      const bookedSlots = store.slots.filter(slot => slot.weekNumber === currentWeek && slot.status === 'booked');
      return count + bookedSlots.length;
    }, 0);
  }, [stores]);
  // Platform KPIs
  const platformStats = useMemo(() => {
    let totalBooked = 0;
    let totalSlots = 0;
    const activeBrands = new Set<string>();
    stores.forEach(store => {
      store.slots.forEach(slot => {
        totalSlots++;
        if (slot.status === 'booked') {
          totalBooked++;
          if (slot.brandName) activeBrands.add(slot.brandName);
        }
      });
    });
    const utilisation = totalSlots > 0 ? Math.round(totalBooked / totalSlots * 100) : 0;
    const pendingCount = brandRequests.filter(r => r.status === 'pending' || r.status === 'brand-accepted').length;
    return {
      totalBooked,
      totalSlots,
      utilisation,
      activeBrandsCount: activeBrands.size,
      pendingCount
    };
  }, [stores, brandRequests]);
  const drafts = brandRequests.filter(r => r.status === 'draft');
  const draftCount = drafts.length;
  const handleBlock = () => {
    setPreBookModal({
      isOpen: true,
      mode: 'block'
    });
  };
  const handlePrebook = () => {
    setPreBookModal({
      isOpen: true,
      mode: 'prebook'
    });
  };
  // Update slots via global state (replaces updateStoreSlots)
  const handleConfirmAction = (mediaTypes: MediaType[], weekNumbers: number[], brandName?: string) => {
    const newStatus: SlotStatus = preBookModal.mode === 'prebook' ? 'booked' : 'blocked';
    let updatedCount = 0;
    stores.forEach(store => {
      if (selectedStores.includes(store.id)) {
        store.slots.forEach(slot => {
          if (mediaTypes.includes(slot.mediaType) && weekNumbers.includes(slot.weekNumber) && slot.status === 'available') {
            onUpdateSlot(store.id, slot.id, {
              status: newStatus,
              brandName: newStatus === 'booked' ? brandName : undefined
            });
            updatedCount++;
          }
        });
      }
    });
    setSelectedStores([]);
    if (updatedCount > 0) {
      showToast(preBookModal.mode === 'prebook' ? `${updatedCount} slot${updatedCount !== 1 ? 's' : ''} booked for ${brandName || 'campaign'}` : `${updatedCount} slot${updatedCount !== 1 ? 's' : ''} blocked successfully`);
    } else {
      showToast('No available slots matched your selection', 'error');
    }
  };
  return <div className="min-h-screen w-full bg-white flex flex-col" data-id="element-1122">
      <Header user={user} onLogout={onLogout} currentView="admin-dashboard" onNavigate={onNavigate} draftCount={drafts.length} stores={stores} brands={brands} brandRequests={brandRequests} activityEntries={activityLog?.entries} unreadCount={activityLog?.unreadCount} onMarkAllRead={activityLog?.markAllAsRead} onMarkRead={activityLog?.markAsRead} onSelectStore={onStoreSelect} data-id="element-1123" />
      <UrgencyBanner bookedCount={bookedCount} data-id="element-1124" />

      {/* KPI Stats */}
      <div className="border-b border-border bg-white px-6 py-5" data-id="element-1125">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1126">
          <div className="flex items-center gap-3 px-4 py-3 bg-surface-warm rounded-lg" data-id="element-1127">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0" data-id="element-1128">
              <BarChart3Icon className="w-5 h-5 text-primary" data-id="element-1129" />
            </div>
            <div data-id="element-1130">
              <div className="text-2xl font-bold text-text-primary" data-id="element-1131">
                {platformStats.totalBooked}
              </div>
              <div className="text-[11px] text-text-tertiary font-medium" data-id="element-1132">
                Booked Slots
              </div>
            </div>
          </div>

          <div className="flex flex-col justify-center px-4 py-3 bg-surface-warm rounded-lg" data-id="element-1133">
            <div className="flex items-center gap-3 mb-2" data-id="element-1134">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0" data-id="element-1135">
                <TrendingUpIcon className="w-5 h-5 text-blue-600" data-id="element-1136" />
              </div>
              <div data-id="element-1137">
                <div className="text-2xl font-bold text-text-primary" data-id="element-1138">
                  {platformStats.utilisation}%
                </div>
                <div className="text-[11px] text-text-tertiary font-medium" data-id="element-1139">
                  Utilisation
                </div>
              </div>
            </div>
            <div className="w-full h-1.5 bg-border rounded-full overflow-hidden" data-id="element-1140">
              <div className={`h-full rounded-full transition-all ${platformStats.utilisation > 75 ? 'bg-green-500' : platformStats.utilisation > 40 ? 'bg-blue-500' : 'bg-text-tertiary'}`} style={{
              width: `${platformStats.utilisation}%`
            }} data-id="element-1141" />
            </div>
          </div>

          <div className="flex items-center gap-3 px-4 py-3 bg-surface-warm rounded-lg" data-id="element-1142">
            <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-1143">
              <UsersIcon className="w-5 h-5 text-purple-600" data-id="element-1144" />
            </div>
            <div data-id="element-1145">
              <div className="text-2xl font-bold text-text-primary" data-id="element-1146">
                {platformStats.activeBrandsCount}
              </div>
              <div className="text-[11px] text-text-tertiary font-medium" data-id="element-1147">
                Active Brands
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 px-4 py-3 bg-surface-warm rounded-lg" data-id="element-1148">
            <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0" data-id="element-1149">
              <SendIcon className="w-5 h-5 text-amber-600" data-id="element-1150" />
            </div>
            <div data-id="element-1151">
              <div className="text-2xl font-bold text-text-primary" data-id="element-1152">
                {platformStats.pendingCount}
              </div>
              <div className="text-[11px] text-text-tertiary font-medium" data-id="element-1153">
                Pending Requests
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Drafts banner */}
      {draftCount > 0 && <div className="border-b border-border bg-surface-warm" data-id="element-1154">
          <button onClick={() => setShowDrafts(v => !v)} className="w-full px-6 py-2.5 flex items-center justify-between hover:bg-surface transition-colors" data-id="element-1155">
            <div className="flex items-center gap-2.5" data-id="element-1156">
              <FileTextIcon className="w-4 h-4 text-text-tertiary" data-id="element-1157" />
              <span className="text-sm text-text-secondary" data-id="element-1158">
                You have{' '}
                <span className="font-semibold text-text-primary" data-id="element-1159">
                  {draftCount} unsent draft{draftCount !== 1 ? 's' : ''}
                </span>{' '}
                waiting to be sent to brands.
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-sm font-medium text-primary" data-id="element-1160">
              {showDrafts ? 'Hide' : 'View Drafts'}
              {showDrafts ? <ChevronUpIcon className="w-4 h-4" data-id="element-1161" /> : <ChevronDownIcon className="w-4 h-4" data-id="element-1162" />}
            </div>
          </button>

          {showDrafts && <div className="px-6 pb-4" data-id="element-1163">
              <div className="bg-white border border-border rounded-sm overflow-hidden overflow-x-auto" data-id="element-1164">
                <table className="w-full text-left border-collapse min-w-[700px]" data-id="element-1165">
                  <thead data-id="element-1166">
                    <tr className="bg-surface border-b border-border" data-id="element-1167">
                      <th className="px-4 py-2 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1168">
                        Brand
                      </th>
                      <th className="px-4 py-2 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1169">
                        Store(s)
                      </th>
                      <th className="px-4 py-2 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1170">
                        Media
                      </th>
                      <th className="px-4 py-2 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1171">
                        Week
                      </th>
                      <th className="px-4 py-2 text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-1172">
                        Campaign
                      </th>
                      <th className="px-4 py-2 text-xs font-semibold text-text-secondary uppercase tracking-wide text-right" data-id="element-1173">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border" data-id="element-1174">
                    {drafts.map(draft => <tr key={draft.id} className="hover:bg-surface/50 transition-colors" data-id="element-1175">
                        <td className="px-4 py-3" data-id="element-1176">
                          <div className="text-sm font-medium text-text-primary" data-id="element-1177">
                            {draft.brandName}
                          </div>
                          {draft.contactName && <div className="text-xs text-text-tertiary mt-0.5" data-id="element-1178">
                              {draft.contactName}
                            </div>}
                        </td>
                        <td className="px-4 py-3 text-sm text-text-secondary" data-id="element-1179">
                          {draft.stores.length === 1 ? draft.stores[0]?.storeName : `${draft.stores.length} stores`}
                        </td>
                        <td className="px-4 py-3 text-xs text-text-secondary" data-id="element-1180">
                          {draft.mediaTypes?.join(', ') || draft.mediaType}
                        </td>
                        <td className="px-4 py-3 text-sm text-text-secondary" data-id="element-1181">
                          Week {draft.weekNumber}
                        </td>
                        <td className="px-4 py-3 text-sm text-text-secondary" data-id="element-1182">
                          {draft.campaignName || <span className="text-text-tertiary" data-id="element-1183">—</span>}
                        </td>
                        <td className="px-4 py-3" data-id="element-1184">
                          <div className="flex items-center justify-end gap-2" data-id="element-1185">
                            <Button variant="secondary" size="sm" onClick={() => {
                      setEditingDraft(draft);
                      setIsRequestModalOpen(true);
                    }} className="flex items-center gap-1.5" data-id="element-1186">
                              <PencilIcon className="w-3 h-3" data-id="element-1187" />
                              Edit
                            </Button>
                            <Button variant="primary" size="sm" onClick={() => {
                      onActivateRequest(draft.id);
                      showToast(`Invitation sent to ${draft.brandName}`);
                    }} className="flex items-center gap-1.5" data-id="element-1188">
                              <SendIcon className="w-3 h-3" data-id="element-1189" />
                              Send to Brand
                            </Button>
                            <Button variant="secondary" size="sm" onClick={() => {
                      onRemoveRequest(draft.id);
                      showToast('Draft removed');
                    }} className="border-urgent/50 text-urgent hover:bg-urgent/5 flex items-center gap-1.5" data-id="element-1190">
                              <Trash2Icon className="w-3 h-3" data-id="element-1191" />
                            </Button>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>}
        </div>}

      <FilterBar filters={filters} onFilterChange={setFilters} data-id="element-1192" />

      {/* Tab Navigation */}
      <div className="px-6 py-3 border-b border-border bg-surface-warm flex items-center gap-2" data-id="element-1193">
        <button onClick={() => setActiveTab('stores')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'stores' ? 'bg-white text-primary shadow-sm border border-border' : 'text-text-secondary hover:text-text-primary hover:bg-surface'}`} data-id="element-1194">
          <ListIcon className="w-4 h-4" data-id="element-1195" />
          Stores List
        </button>
        <button onClick={() => setActiveTab('calendar')} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeTab === 'calendar' ? 'bg-white text-primary shadow-sm border border-border' : 'text-text-secondary hover:text-text-primary hover:bg-surface'}`} data-id="element-1196">
          <CalendarDaysIcon className="w-4 h-4" data-id="element-1197" />
          Calendar View
        </button>
      </div>

      <main className="flex-1 flex flex-col overflow-hidden" data-id="element-1198">
        {activeTab === 'stores' ? <>
            <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-white sticky top-0 z-20" data-id="element-1199">
              <div className="flex items-center gap-4" data-id="element-1200">
                <h2 className="text-lg font-bold text-text-primary" data-id="element-1201">
                  {selectedStores.length > 0 ? <span data-id="element-1202">
                      <span className="text-primary" data-id="element-1203">
                        {selectedStores.length}
                      </span>{' '}
                      of {filteredStores.length} stores selected
                    </span> : <span data-id="element-1204">
                      Showing{' '}
                      <span className="text-primary" data-id="element-1205">
                        {filteredStores.length}
                      </span>{' '}
                      of {stores.length} stores
                    </span>}
                </h2>
                {selectedStores.length > 0 && <span className="text-sm text-text-tertiary" data-id="element-1206">
                    • {selectedStores.length} selected
                  </span>}
              </div>

              <div className="flex items-center gap-2" data-id="element-1207">
                <Tooltip content="Block inventory to prevent brands from booking" position="bottom" showOnFirstVisit tooltipKey="block-inventory" data-id="element-1208">
                  <Button variant="secondary" size="sm" onClick={handleBlock} className="flex items-center gap-1.5" data-id="element-1209">
                    <LockIcon className="w-3.5 h-3.5" data-id="element-1210" />
                    Block Inventory
                  </Button>
                </Tooltip>

                <Tooltip content="Book media space for a campaign already confirmed with the brand" position="bottom" showOnFirstVisit tooltipKey="lock-campaign" data-id="element-1211">
                  <Button variant="secondary" size="sm" onClick={handlePrebook} className="flex items-center gap-1.5" data-id="element-1212">
                    <CalendarPlusIcon className="w-3.5 h-3.5" data-id="element-1213" />
                    Book Confirmed Campaign
                  </Button>
                </Tooltip>

                <div className="w-px h-6 bg-border mx-1" data-id="element-1214" />

                <Tooltip content="Send an invitation to a brand to book selected stores" position="bottom" showOnFirstVisit tooltipKey="invite-brand" data-id="element-1215">
                  <Button variant="primary" size="sm" onClick={() => setIsRequestModalOpen(true)} className="flex items-center gap-1.5" data-id="element-1216">
                    <SendIcon className="w-3.5 h-3.5" data-id="element-1217" />
                    Invite Brand to Book
                  </Button>
                </Tooltip>
              </div>
            </div>

            {/* Store table */}
            <div className="flex-1 overflow-auto" data-id="element-1218">
              <StoreTable stores={filteredStores} onStoreClick={onStoreSelect} activeMediaType={filters.mediaType} availabilityFilter={filters.availability} selectable={true} selectedStoreIds={selectedStores} onSelectionChange={setSelectedStores} data-id="element-1219" />
            </div>
          </> : <div className="flex-1 overflow-auto" data-id="element-1220">
            <DashboardCalendar stores={stores} data-id="element-1221" />
          </div>}
      </main>

      <BrandRequestModal isOpen={isRequestModalOpen} stores={stores} editingDraft={editingDraft} preSelectedStoreIds={selectedStores} onClose={() => {
      setIsRequestModalOpen(false);
      setEditingDraft(null);
    }} onSendRequest={onSendRequest} onUpdateRequest={onUpdateRequest} data-id="element-1222" />

      <PreBookModal isOpen={preBookModal.isOpen} mode={preBookModal.mode} selectedCount={selectedStores.length} stores={stores} onClose={() => setPreBookModal({
      isOpen: false,
      mode: 'prebook'
    })} onConfirm={handleConfirmAction} data-id="element-1223" />

      {/* Toast notification */}
      {toast && <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-4 fade-in duration-200" data-id="element-1224">
          <div className={`flex items-center gap-2.5 px-4 py-3 rounded-lg shadow-lg border ${toast.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-id="element-1225">
            <CheckCircleIcon className="w-4 h-4 flex-shrink-0" data-id="element-1226" />
            <span className="text-sm font-medium" data-id="element-1227">{toast.message}</span>
          </div>
        </div>}
    </div>;
}